import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Wallet, Monitor, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import CursorGlow from "../../components/CursorGlow";

const principles = [
  {
    icon: MessageSquare,
    title: "Simple language, no jargon",
    description: "We speak your language, not accountant-speak."
  },
  {
    icon: Wallet,
    title: "AI-powered insights",
    description: "Get actionable recommendations from your financial data."
  },
  {
    icon: Monitor,
    title: "Designed for daily use",
    description: "Quick actions, instant updates, no learning curve."
  },
  {
    icon: Sparkles,
    title: "Intelligent automation",
    description: "Let AI handle the repetitive work for you."
  }
];

export default function About() {
  return (
    <div className="min-h-screen bg-emerald-950 text-white overflow-hidden">
      <CursorGlow />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-emerald-950/90 backdrop-blur-md border-b border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 sm:h-24 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-10">
              <Link to="/" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Home</Link>
              <Link to="/features" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm font-medium text-emerald-300 hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" size="sm" className="text-emerald-300 hover:text-white hover:bg-emerald-800/50">Log in</Button></Link>
              <Link to="/pricing"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-6">Sign up</Button></Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-5xl sm:text-6xl font-bold mb-8">
            About
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-300">
              LedgerAMT
            </span>
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8 text-center">
            <p className="text-2xl text-emerald-200/80 leading-relaxed">
              LedgerAMT is an AI-powered accounting platform built for modern businesses.
            </p>
            
            <p className="text-lg text-emerald-300/70 leading-relaxed">
              Most small businesses issue invoices and collect payments, but still struggle to clearly answer:
            </p>

            <div className="flex flex-wrap justify-center gap-4 my-12">
              <div className="bg-emerald-900/50 rounded-2xl px-6 py-4 border border-emerald-700/30">
                <span className="text-emerald-400 text-lg">What's pending?</span>
              </div>
              <div className="bg-emerald-900/50 rounded-2xl px-6 py-4 border border-emerald-700/30">
                <span className="text-emerald-400 text-lg">What's overdue?</span>
              </div>
              <div className="bg-emerald-900/50 rounded-2xl px-6 py-4 border border-emerald-700/30">
                <span className="text-emerald-400 text-lg">What's actually cash in hand?</span>
              </div>
            </div>

            <p className="text-lg text-emerald-200/80 leading-relaxed max-w-3xl mx-auto">
              LedgerAMT solves this with <span className="text-emerald-400 font-semibold">AI-powered automation</span>: 
              Create invoices, track payments, get intelligent insights, and maintain clear cash flow—all 
              powered by artificial intelligence.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-900/20 to-transparent"></div>
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold">
              Our <span className="text-emerald-400">Principles</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/30">
                  <principle.icon className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{principle.title}</h3>
                <p className="text-emerald-300/70 text-sm">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-4xl font-bold mb-6">
            Ready to simplify your <span className="text-emerald-400">finances</span>?
          </h2>
          <p className="text-emerald-300/70 text-lg mb-10">
            Start with a plan that fits your business.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-lg px-10 h-14 rounded-xl shadow-lg shadow-emerald-500/30">
              View pricing
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-950 border-t border-emerald-800/30 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 w-auto mb-6" />
              <p className="text-emerald-400/60 text-sm">Simple invoices. Clear cash control.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/features" className="hover:text-emerald-400">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/about" className="hover:text-emerald-400">About</Link></li>
                <li><Link to="/contact" className="hover:text-emerald-400">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Account</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/login" className="hover:text-emerald-400">Log in</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Sign up</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-emerald-800/30 pt-8 text-center text-sm text-emerald-400/50">
            <p>© {new Date().getFullYear()} LedgerAMT. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
